import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Alert,
  Table,
  Form,
} from "react-bootstrap";
import {
  FaPlusCircle,
  FaUsers,
  FaEdit,
  FaTrash,
  FaSave,
  FaChartLine,
  FaCalendarAlt,
  FaClock,
} from "react-icons/fa";

import { db } from "../firebase";
import Header from "../components/Header";

function AdminDashboard() {
  const navigate = useNavigate();

  const [stockCalls, setStockCalls] = useState([]);
  const [quickUpdates, setQuickUpdates] = useState({});
  const [loading, setLoading] = useState(true);

  const sortCallsActiveFirst = (calls) => {
    return [...calls].sort((a, b) => {
      const aIsActive = (a.status || "Active") === "Active";
      const bIsActive = (b.status || "Active") === "Active";

      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;

      return (
        Number(b.createdAt || b.updatedAt || 0) -
        Number(a.createdAt || a.updatedAt || 0)
      );
    });
  };

  const loadStockCalls = async () => {
    try {
      setLoading(true);

      const data = await getDocs(collection(db, "stockCalls"));

      const callsList = data.docs.map((document) => ({
        id: document.id,
        ...document.data(),
      }));

      const sortedCalls = sortCallsActiveFirst(callsList);

      const updateValues = {};

      sortedCalls.forEach((call) => {
        updateValues[call.id] = {
          currentPrice: call.currentPrice || "",
          status: call.status || "Active",
        };
      });

      setStockCalls(sortedCalls);
      setQuickUpdates(updateValues);
      setLoading(false);
    } catch (error) {
      console.error(error);
      alert("Unable to load stock calls.");
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStockCalls();
  }, []);

  const updateQuickValue = (callId, field, value) => {
    setQuickUpdates((previous) => ({
      ...previous,
      [callId]: {
        ...previous[callId],
        [field]: value,
      },
    }));
  };

  const quickUpdateCall = async (call) => {
    try {
      const values = quickUpdates[call.id];

      if (!values) {
        alert("No update values found.");
        return;
      }

      await updateDoc(doc(db, "stockCalls", call.id), {
        currentPrice: values.currentPrice,
        status: values.status,
        updatedAt: Date.now(),
      });

      alert("✅ Stock call updated successfully.");
      loadStockCalls();
    } catch (error) {
      console.error(error);
      alert("Unable to update stock call.");
    }
  };

  const deleteStockCall = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this stock call?"
    );

    if (!confirmDelete) return;

    await deleteDoc(doc(db, "stockCalls", id));

    alert("Stock call deleted successfully.");
    loadStockCalls();
  };

  const editStockCall = (call) => {
    localStorage.setItem("editCallId", call.id);

    navigate("/edit-call", {
      state: {
        callId: call.id,
        stockCallId: call.id,
        call,
      },
    });
  };

  const getProfitLoss = (call) => {
    const entryPrice = Number(call.entryPrice || 0);
    const currentPrice = Number(call.currentPrice || entryPrice || 0);

    const amount = currentPrice - entryPrice;

    const percent =
      entryPrice > 0 ? ((currentPrice - entryPrice) / entryPrice) * 100 : 0;

    return {
      amount: Number(amount.toFixed(2)),
      percent: Number(percent.toFixed(2)),
    };
  };

  const getStatusBadge = (status) => {
    const finalStatus = status || "Active";

    if (finalStatus === "Active") {
      return (
        <Badge bg="success" className="p-2">
          🟢 Active
        </Badge>
      );
    }

    if (finalStatus === "Target Hit") {
      return (
        <Badge bg="primary" className="p-2">
          🎯 Target Hit
        </Badge>
      );
    }

    if (finalStatus === "Exit") {
      return (
        <Badge bg="warning" text="dark" className="p-2">
          ⏹ Exit
        </Badge>
      );
    }

    if (finalStatus === "Closed") {
      return (
        <Badge bg="secondary" className="p-2">
          Closed
        </Badge>
      );
    }

    return (
      <Badge bg="secondary" className="p-2">
        {finalStatus}
      </Badge>
    );
  };

  const totalCalls = stockCalls.length;

  const activeCalls = stockCalls.filter(
    (call) => (call.status || "Active") === "Active"
  ).length;

  const closedCalls = stockCalls.filter(
    (call) => (call.status || "Active") !== "Active"
  ).length;

  const longTermCalls = stockCalls.filter(
    (call) => call.callType === "Long Term"
  ).length;

  const midTermCalls = stockCalls.filter(
    (call) => call.callType === "Mid Term"
  ).length;

  const shortTermCalls = stockCalls.filter(
    (call) => call.callType === "Short Term"
  ).length;

  return (
    <>
      <Header />

      <Container fluid className="mt-4 mb-5 px-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="fw-bold mb-1">Admin Dashboard</h2>
            <p className="text-muted mb-0">
              Active running calls are shown first.
            </p>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={3} xs={6} className="mb-3">
            <Card className="shadow border-0 h-100">
              <Card.Body>
                <small className="text-muted">Total Calls</small>
                <h3 className="fw-bold mb-0">{totalCalls}</h3>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} xs={6} className="mb-3">
            <Card className="shadow border-0 h-100">
              <Card.Body>
                <small className="text-muted">Active Calls</small>
                <h3 className="fw-bold text-success mb-0">{activeCalls}</h3>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} xs={6} className="mb-3">
            <Card className="shadow border-0 h-100">
              <Card.Body>
                <small className="text-muted">Closed / Exit / Target</small>
                <h3 className="fw-bold text-secondary mb-0">{closedCalls}</h3>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} xs={6} className="mb-3">
            <Card className="shadow border-0 h-100">
              <Card.Body>
                <small className="text-muted">Long / Mid / Short</small>
                <h5 className="fw-bold mb-0">
                  {longTermCalls} / {midTermCalls} / {shortTermCalls}
                </h5>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="shadow border-0 mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={6}>
                <h4 className="fw-bold mb-1">Quick Actions</h4>
                <p className="text-muted mb-0">
                  Add calls or manage customer subscriptions.
                </p>
              </Col>

              <Col md={6} className="text-md-end mt-3 mt-md-0">
                <Button
                  variant="success"
                  className="me-2 mb-2"
                  onClick={() => navigate("/add-call")}
                >
                  <FaPlusCircle className="me-2" />
                  Add Stock Call
                </Button>

                <Button
                  variant="outline-dark"
                  className="mb-2"
                  onClick={() => navigate("/manage-customers")}
                >
                  <FaUsers className="me-2" />
                  Manage Customers
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="shadow border-0">
          <Card.Body>
            <h4 className="fw-bold mb-4">
              📋 Recent Stock Calls
            </h4>

            {loading ? (
              <Alert variant="info">Loading stock calls...</Alert>
            ) : stockCalls.length === 0 ? (
              <Alert variant="warning">No stock calls added yet.</Alert>
            ) : (
              <div className="table-responsive">
                <Table bordered hover className="align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th>Stock</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Date & Time</th>
                      <th>Buy</th>
                      <th>Calc Price</th>
                      <th>CMP</th>
                      <th>P/L %</th>
                      <th>P/L ₹</th>
                      <th>Target</th>
                      <th>SL</th>
                      <th>Quick Update</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {stockCalls.map((call) => {
                      const profitLoss = getProfitLoss(call);
                      const quickValue = quickUpdates[call.id] || {};

                      return (
                        <tr key={call.id}>
                          <td>
                            <strong>{call.stockName}</strong>
                          </td>

                          <td>
                            <Badge
                              bg={
                                call.callType === "Short Term"
                                  ? "danger"
                                  : "success"
                              }
                              className="p-2"
                            >
                              {call.callType}
                            </Badge>
                          </td>

                          <td>{getStatusBadge(call.status)}</td>

                          <td>
                            <FaCalendarAlt className="me-1 text-primary" />
                            {call.recommendationDate || "-"}
                            <br />
                            <FaClock className="me-1 text-secondary" />
                            {call.recommendationTime || "-"}
                          </td>

                          <td>
                            <strong>{call.buyPrice || "-"}</strong>
                          </td>

                          <td>₹ {call.entryPrice || "-"}</td>

                          <td>
                            <strong>₹ {call.currentPrice || "-"}</strong>
                          </td>

                          <td>
                            <strong
                              className={
                                profitLoss.percent >= 0
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {profitLoss.percent >= 0 ? "+" : ""}
                              {profitLoss.percent}%
                            </strong>
                          </td>

                          <td>
                            <strong
                              className={
                                profitLoss.amount >= 0
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {profitLoss.amount >= 0 ? "+" : ""}
                              ₹{profitLoss.amount}
                            </strong>
                          </td>

                          <td>{call.targetPrice || "-"}</td>

                          <td>{call.stopLoss || "-"}</td>

                          <td style={{ minWidth: "230px" }}>
                            <Form.Control
                              type="number"
                              className="mb-2"
                              placeholder="Current price"
                              value={quickValue.currentPrice || ""}
                              onChange={(event) =>
                                updateQuickValue(
                                  call.id,
                                  "currentPrice",
                                  event.target.value
                                )
                              }
                            />

                            <Form.Select
                              className="mb-2"
                              value={quickValue.status || "Active"}
                              onChange={(event) =>
                                updateQuickValue(
                                  call.id,
                                  "status",
                                  event.target.value
                                )
                              }
                            >
                              <option>Active</option>
                              <option>Target Hit</option>
                              <option>Exit</option>
                              <option>Closed</option>
                            </Form.Select>

                            <Button
                              variant="success"
                              className="w-100"
                              onClick={() => quickUpdateCall(call)}
                            >
                              <FaSave className="me-2" />
                              Update
                            </Button>
                          </td>

                          <td style={{ minWidth: "100px" }}>
                            <Button
                              variant="warning"
                              size="sm"
                              className="me-2 mb-2"
                              onClick={() => editStockCall(call)}
                            >
                              <FaEdit />
                            </Button>

                            <Button
                              variant="danger"
                              size="sm"
                              className="mb-2"
                              onClick={() => deleteStockCall(call.id)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}

export default AdminDashboard;